import { devModeMocks } from "@api/core-api-utils";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import {
  BaseCompanyIdProps,
  BaseCompanyUserIdentityProps,
} from "@custom-types/base-types";
import { BaseMemberAndTeam } from "@custom-types/member-types";
import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  FetchCompanyMemberDetailsProps,
  RemoveMemberProps,
  UpdateMemberRoleProps,
  UpdateMemberRoleResult,
} from "@src/store/members/members-slice-helper";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { RootState } from "@store/store-helper";

/**
 * Fetches company members from the backend so they can be put into the store
 * @throws {HttpUtils.FetchError} if the Core API client request fails and not in dev mode
 */
export const fetchCompanyMembers = createAsyncThunk<
  FaroCoreApiTypes.CompanyMemberBase[],
  BaseCompanyIdProps,
  { state: RootState }
>(
  "members/fetchCompanyMembers",
  async ({ companyId }, { getState }) => {
    const {
      app: { isDevModeEnabled },
    } = getState();

    try {
      /**
       * Note: Currently, we are fetching all users from the endpoint, including user groups.
       * Once the backend updates the teams endpoint with roles,
       * we can remove user groups from this fetch to optimize the request.
       */
      return await faroCoreApiClient.company.getMembersInCompany({
        companyId,
        types: ["user", "user-group", "user-invite"],
      });
    } catch (error) {
      if (isDevModeEnabled) {
        return devModeMocks.members;
      }

      throw error;
    }
  },
  {
    condition: (_, api) => {
      const { hasFetchedWorkspaceMembers } = api.getState().members.fetched;
      const { isFetchingCompanyMembers } = api.getState().members.fetching;

      // Skip fetching if members have already been fetched or fetching is in progress
      return !hasFetchedWorkspaceMembers && !isFetchingCompanyMembers;
    },
  }
);

/**
 * Fetches the details of a company member so it can be put into the members store
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchCompanyMemberDetails = createAsyncThunk<
  FaroCoreApiTypes.CompanyMemberDetails,
  FetchCompanyMemberDetailsProps
>("members/fetchCompanyMemberDetails", async ({ companyId, identity }) => {
  return await faroCoreApiClient.company.getMemberDetailsInCompany({
    companyId,
    identity,
  });
});

/**
 * Remove a member from the company using the backend and then removes it from the store as well
 * @throws {Error} if no companyId or member email exist to remove the member from the workspace
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const removeMemberFromCompany = createAsyncThunk<
  BaseMemberAndTeam,
  RemoveMemberProps
>("members/removeCompanyMember", async ({ companyId, member }) => {
  if (!companyId) {
    throw new Error("No companyId was given to remove member from workspace!");
  }

  if (!member?.email) {
    throw new Error("No email was given to remove member from workspace!");
  }

  await faroCoreApiClient.company.removeMemberFromCompany({
    companyId,
    userIdentity: member.email,
  });

  return member;
});

/**
 * Update a member role in the workspace using the backend,
 * and then update it in the store as well
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const updateMemberRoleInWorkspace = createAsyncThunk<
  UpdateMemberRoleResult,
  UpdateMemberRoleProps
>(
  "members/updateMemberRoleInWorkspace",
  async ({ companyId, role, identity }) => {
    const memberData = {
      role,
      identity,
    };

    const result = await faroCoreApiClient.company.updateCompanyMember({
      companyId,
      identity,
      payload: memberData,
    });

    return {
      role: result.role,
      identity: result.identity,
    };
  }
);

interface SetMemberPermissionsInCompanyPayload
  extends BaseCompanyUserIdentityProps {
  /** Updated permission */
  permissions: FaroCoreApiTypes.UserPermission[];
}

/**
 * Set the permissions of a member in the company using the backend.
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const setMemberPermissionsInCompany = createAsyncThunk<
  FaroCoreApiTypes.SetMemberPermissionsInCompany,
  SetMemberPermissionsInCompanyPayload
>(
  "members/setMemberPermissionsInCompany",
  async ({ companyId, userIdentity, permissions }) => {
    return await faroCoreApiClient.company.setMemberPermissionsInCompany({
      companyId,
      userIdentity,
      payload: { permissions },
    });
  }
);
